import { ConfirmDeleteDialog, ConfirmDialogProps } from '@ardoq/modal';
import { WarningNotification } from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import { I18nLocale, Trans, t } from '@ardoq/i18n';
import { DeleteType } from 'components/DeleteAssetModal/types';

type AssetInUseByHomePageDialogProps = ConfirmDialogProps & {
  deleteType: DeleteType;
  assetUsedInHomePageName: string;
  i18nLocale: I18nLocale;
};

const getAssetLabel = (
  deleteType: DeleteType,
  i18nLocale: I18nLocale
): string | undefined => {
  switch (deleteType) {
    case DeleteType.DASHBOARD:
      return t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.dashboard',
        'dashboard'
      );
    case DeleteType.REPORT:
      return t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.report',
        'report'
      );
    case DeleteType.SURVEY:
      return t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.survey',
        'survey'
      );
    case DeleteType.PRESENTATION:
      return t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.presentation',
        'presentation'
      );
    case DeleteType.TRAVERSAL:
      return t(
        i18nLocale,
        'assetsBrowser:modals.delete.itemType.viewpoint',
        'viewpoint'
      );
    default:
      return undefined;
  }
};

export const AssetInUseByHomePageDialog = ({
  assetUsedInHomePageName,
  deleteType,
  i18nLocale,
  ...commonDeleteProps
}: AssetInUseByHomePageDialogProps) => {
  const isDeletingMultipleAssets = deleteType === DeleteType.MULTIPLE;
  const assetLabel = getAssetLabel(deleteType, i18nLocale) ?? 'asset';
  const assetNameForWarning = isDeletingMultipleAssets
    ? assetUsedInHomePageName
    : t(
        i18nLocale,
        'assetsBrowser:modals.delete.homepageWarning.this',
        { assetLabel },
        'This {{assetLabel}}'
      );
  return (
    <ConfirmDeleteDialog
      {...commonDeleteProps}
      subtitle={
        isDeletingMultipleAssets ? commonDeleteProps.subtitle : undefined
      }
      text2={
        <WarningNotification>
          <Trans
            i18nKey="assetsBrowser:modals.delete.homepageWarning.warningText"
            values={{ assetNameForWarning }}
            defaults="<bold>{{assetNameForWarning}}</bold> is used on your organization's Home page. If it's deleted, the linked action will be empty for all users."
            components={{ bold: <b /> }}
          />
        </WarningNotification>
      }
      title={
        isDeletingMultipleAssets
          ? commonDeleteProps.title
          : t(
              i18nLocale,
              'assetsBrowser:modals.delete.homepageWarning.title',
              { assetLabel },
              'Delete {{assetLabel}} permanently'
            )
      }
      text={
        isDeletingMultipleAssets ? (
          commonDeleteProps.text
        ) : (
          <Text>
            <Trans
              i18nKey="assetsBrowser:modals.delete.homepageWarning.text"
              defaults="You're about to delete <bold>{{assetUsedInHomePageName}}</bold> {{assetLabel}}. This deletion can't be undone."
              values={{ assetUsedInHomePageName, assetLabel }}
              components={{ bold: <b /> }}
            />
          </Text>
        )
      }
      warningBlockMessage={
        isDeletingMultipleAssets
          ? commonDeleteProps.warningBlockMessage
          : undefined
      }
      confirmButtonTitle={
        isDeletingMultipleAssets
          ? commonDeleteProps.confirmButtonTitle
          : t(
              i18nLocale,
              'assetsBrowser:modals.delete.homepageWarning.delete',
              { assetLabel },
              'Delete {{assetLabel}}'
            )
      }
      cancelButtonTitle={
        isDeletingMultipleAssets
          ? commonDeleteProps.cancelButtonTitle
          : t(
              i18nLocale,
              'assetsBrowser:modals.delete.homepageWarning.keep',
              { assetLabel },
              'Keep {{assetLabel}}'
            )
      }
    />
  );
};
